import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ProcedureFilter, ProcedureMatch } from '@/lib/procedureFilters';

interface DateRange {
    from?: string; // ISO string for persistence
    to?: string;   // ISO string for persistence
}

interface CallsState {
    /** #46 — recherche libre : identifiant d'appel, nom de fichier ou résumé. */
    searchTerm: string;
    convertedFilter: string | null;
    resolutionFilter: string[];
    dateRange: DateRange | undefined;
    selectedReasonParents: string[];
    selectedReasonChildren: string[];
    segmentFilter: string[];
    selectedAgentIds: string[];
    selectedTeams: string[];
    campaignFilter: string[];
    reiterationFilter: string[];
    selectedProducts: string[];
    /** #69 — conditions sur les procédures, valables pour le projet indiqué. */
    procedureFilters: ProcedureFilter[];
    procedureMatch: ProcedureMatch;
    procedureFiltersProjectId: number | null;
    sortColumn: string;
    sortDirection: 'asc' | 'desc';
    currentPage: number;
    pageSize: number;
    /** Retour à la liste : position de défilement au moment d'ouvrir une fiche. */
    listScrollTop: number | null;
    /** Dernier appel consulté, mis en évidence et focalisé au retour. */
    lastViewedCallId: number | null;
    /** Appels de la page affichée, dans l'ordre : navigation précédent / suivant depuis la fiche. */
    resultIds: number[];
    /** Rang du premier appel de `resultIds` dans l'ensemble des résultats (base 1). */
    resultOffset: number;
    resultTotal: number;
}

/** Préférences d'affichage du tableau, conservées d'une session à l'autre. */
export type CallsColumnKey = 'campagne' | 'produit' | 'segment' | 'motif' | 'resultat' | 'qualite' | 'satisfaction' | 'reiteration' | 'duree';
export type TableDensity = 'confortable' | 'compacte';

interface CallsTablePreferences {
    visibleColumns: CallsColumnKey[];
    density: TableDensity;
    setVisibleColumns: (columns: CallsColumnKey[]) => void;
    setDensity: (density: TableDensity) => void;
}

/** Colonnes utiles pour choisir un appel ; les autres restent accessibles via « Colonnes ». */
export const DEFAULT_CALLS_COLUMNS: CallsColumnKey[] = ['motif', 'resultat', 'qualite', 'satisfaction', 'reiteration', 'duree'];

export const useCallsTablePreferences = create<CallsTablePreferences>()(
    persist(
        (set) => ({
            visibleColumns: DEFAULT_CALLS_COLUMNS,
            density: 'confortable',
            setVisibleColumns: (visibleColumns) => set({ visibleColumns }),
            setDensity: (density) => set({ density }),
        }),
        {
            name: 'calls-table-preferences',
            storage: createJSONStorage(() => localStorage),
        }
    )
);

interface AgentsState {
    currentPage: number;
    startDate?: string;
    endDate?: string;
    sortField: string;
    sortDirection: 'asc' | 'desc';
    selectedAgentIds: string[];
    selectedTeams: string[];
}

interface NavigationStore {
    calls: CallsState;
    agents: AgentsState;

    // Actions for Calls
    setCallsState: (state: Partial<CallsState>) => void;
    resetCallsFilters: () => void;

    // Actions for Agents
    setAgentsState: (state: Partial<AgentsState>) => void;
    resetAgentsFilters: () => void;
}

/** Période par défaut de la liste d'appels (90 jours). La vue d'ensemble
 *  transmet sa propre période quand elle renvoie vers la liste. */
export const DEFAULT_CALLS_PERIOD_DAYS = 90;

export const defaultCallsDateRange = (): DateRange => ({
    from: new Date(new Date().setDate(new Date().getDate() - DEFAULT_CALLS_PERIOD_DAYS)).toISOString(),
    to: new Date().toISOString(),
});

const initialCallsState: CallsState = {
    searchTerm: '',
    convertedFilter: null,
    resolutionFilter: [],
    dateRange: defaultCallsDateRange(),
    selectedReasonParents: [],
    selectedReasonChildren: [],
    segmentFilter: [],
    selectedAgentIds: [],
    selectedTeams: [],
    campaignFilter: [],
    reiterationFilter: [],
    selectedProducts: [],
    procedureFilters: [],
    procedureMatch: 'all',
    procedureFiltersProjectId: null,
    sortColumn: 'date',
    sortDirection: 'desc',
    currentPage: 1,
    pageSize: 25,
    listScrollTop: null,
    lastViewedCallId: null,
    resultIds: [],
    resultOffset: 1,
    resultTotal: 0,
};

/** Critères de filtre seuls : ce que « Tout effacer » remet à zéro, sans
 *  toucher à la pagination, au tri ni à la mémoire de retour. */
export const CALLS_FILTER_DEFAULTS = {
    searchTerm: '',
    convertedFilter: null,
    resolutionFilter: [],
    selectedReasonParents: [],
    selectedReasonChildren: [],
    segmentFilter: [],
    selectedAgentIds: [],
    selectedTeams: [],
    campaignFilter: [],
    reiterationFilter: [],
    selectedProducts: [],
    procedureFilters: [],
    procedureMatch: 'all',
} satisfies Partial<CallsState>;

export type { CallsState };

// Même période par défaut que la liste d'appels : sans borne, la page
// agrégeait tout l'historique du projet au premier affichage.
const initialAgentsState: AgentsState = {
    currentPage: 1,
    startDate: defaultCallsDateRange().from,
    endDate: defaultCallsDateRange().to,
    sortField: 'performance',
    sortDirection: 'desc',
    selectedAgentIds: [],
    selectedTeams: [],
};

export const useNavigationStore = create<NavigationStore>()(
    persist(
        (set) => ({
            calls: initialCallsState,
            agents: initialAgentsState,

            setCallsState: (newState) =>
                set((state) => ({
                    calls: { ...state.calls, ...newState }
                })),

            // La période par défaut est recalculée : « 90 derniers jours » doit
            // finir aujourd'hui, pas le jour du chargement du module.
            resetCallsFilters: () =>
                set((state) => ({
                    calls: {
                        ...initialCallsState,
                        dateRange: defaultCallsDateRange(),
                        pageSize: state.calls.pageSize,
                    }
                })),

            setAgentsState: (newState) =>
                set((state) => ({
                    agents: { ...state.agents, ...newState }
                })),

            resetAgentsFilters: () =>
                set(() => {
                    const range = defaultCallsDateRange();
                    return { agents: { ...initialAgentsState, startDate: range.from, endDate: range.to } };
                }),
        }),
        {
            name: 'navigation-memory-storage',
            storage: createJSONStorage(() => sessionStorage),
            // Un état enregistré avant l'ajout d'un champ ne doit pas l'effacer :
            // on complète chaque branche avec ses valeurs par défaut.
            merge: (persisted, current) => {
                const saved = (persisted ?? {}) as Partial<NavigationStore>;
                return {
                    ...current,
                    calls: { ...current.calls, ...saved.calls },
                    agents: { ...current.agents, ...saved.agents },
                };
            },
        }
    )
);
